import { Component, Input, OnChanges, Output } from "@angular/core";
import { User } from "./user-model";
import { Subject } from "rxjs";
import { ConfirmationService, PrimeIcons } from "primeng/api";

@Component({
    selector: 'app-users-table',
    template: `
  <p-confirmDialog
      header="Confirmation"
      icon="pi pi-exclamation-triangle"
  ></p-confirmDialog>
  <div class="flex justify-start mb-4">
    <span class="w-1/3">
      <input
        pInputText
        type="text"
        placeholder="Search users"
        (input)="usersTable.filterGlobal($event.target.value, 'contains')"
      />
    </span>
  </div>

  <p-table
    #usersTable
    selectionMode="single"
    [value]="users"
    [globalFilterFields]="[
      'profile.firstName',
      'profile.lastName',
      'profile.email',
      'profile.jobTitle'
    ]"
    scrollHeight="800"
  >
    <ng-template pTemplate="header">
      <tr>
        <th pSortableColumn="profile.email" class="px-4 py-2">Email</th>
        <th pSortableColumn="profile.firstName" class="px-4 py-2">First Name</th>
        <th pSortableColumn="profile.lastName" class="px-4 py-2">Last Name</th>
        <th pSortableColumn="profile.jobTitle" class="px-4 py-2">Job Title</th>
        <th pSortableColumn="isScimUser" class="px-4 py-2">Source</th>
        <th pSortableColumn="profile.isLicensed" class="px-4 py-2">Licensed</th>
        <th class="px-4 py-2"></th>
      </tr>
    </ng-template>

    <ng-template let-user pTemplate="body">
      <tr
        class="h-20"
        (dblclick)="editUser(user)"
        [pSelectableRow]="user"
      >
        <td class="px-4 py-2">{{ user.profile.email }}</td>
        <td class="px-4 py-2">{{ user.profile.firstName }}</td>
        <td class="px-4 py-2">{{ user.profile.lastName }}</td>
        <td class="px-4 py-2">{{ user.profile.jobTitle }}</td>
        <td class="px-4 py-2">
          @if(user.isScimUser) {
            <p-chip [label]="'Entra-ID'" styleClass="text-xs" severity="info" />
          }@else{
            <p-chip [label]="'Database'" styleClass="text-xs" severity="secondary" />
          }
        </td>
        <td class="px-4 py-2">{{ user.isLicensed }}</td>
        <td class="px-4 py-2">
          <p-splitButton
            class="pr-4"
            (onDropdownClick)="setUser(user)"
            (onClick)="editUser(user)"
            [model]="editOptions"
            icon="pi pi-pencil"
            appendTo="body"
            size="small"
          >
          </p-splitButton>
        </td>
      </tr>
    </ng-template>
  </p-table>

`,
    styles: [``],
    providers: [ConfirmationService],
    standalone: false
})
export class UsersTableComponent implements OnChanges {

  showEditUserForm: boolean;

  @Input() users: User[];
  @Input() user: User;

  ngOnChanges(): void {
    this.editOptions = [
      { label: 'View Designs', icon: PrimeIcons.FILE, command: () => this.openViewDesigns() },
      { label: 'Remove', icon: PrimeIcons.TRASH, command: () => this.removeUser() },
      { label: 'Toggle License', icon: PrimeIcons.REFRESH, command: () => this.toggleLicense() },
      { label: 'Permissions', icon: PrimeIcons.LOCK, command: () => this.editPermissions() },
      { label: 'Send Invite Email', icon: PrimeIcons.ENVELOPE, command: () => this.sendInvite() }
    ];

    if (this.user?.profile?.isGlobalAdmin !== true && this.user?.roles.admin !== true) {
      this.editOptions = this.editOptions.filter(option => option.label !== 'Permissions');
    }
    
    // Only show View Designs for Altus users
    if (this.user?.profile?.organization !== 'Altus') {
      this.editOptions = this.editOptions.filter(option => option.label !== 'View Designs');
    }
  }

  // Update edit options based on selected user
  updateEditOptionsForUser(user: User): void {
    // For SCIM users, disable Permissions option (roles managed by AD groups)
    if (user.isScimUser) {
      this.editOptions = this.editOptions.map(option => 
        option.label === 'Permissions' 
          ? { ...option, disabled: true, title: 'Roles managed by IT via AD groups' }
          : option
      );
    }
  }

  @Output() userEdited = new Subject<User>();
  @Output() userRemoved = new Subject<User>();
  @Output() permissionsEdited = new Subject<User>();
  @Output() licenseToggled = new Subject<User>();
  @Output() inviteSent = new Subject<User>();
  @Output() userSelected = new Subject<User>();
  @Output() viewDesigns = new Subject<User>();

  constructor(private _confirmationService: ConfirmationService) { }

  public editOptions: any[] = [];

  selectedUser: User;

  setUser(user: User) {
    this.selectedUser = user;
    this.updateEditOptionsForUser(user);
  }

  editUser(user: User) {
    this.setUser(user);
    this.userEdited.next(user);
  }

  removeUser() {
    this._confirmationService.confirm({
      message: 'Are you sure that you want to remove this user?',
      accept: () => {
        this.userRemoved.next(this.selectedUser);
      }
    });
  }

  editPermissions() {
    this.permissionsEdited.next(this.selectedUser);
  }

  toggleLicense() {
    this.licenseToggled.next(this.selectedUser);
  }

  sendInvite() {
    this.inviteSent.next(this.selectedUser);
  }

  openViewDesigns() {
    this.viewDesigns.next(this.selectedUser);
  }
  
}
